import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Copy, Trash2, Loader2, Image as ImageIcon, Video, Quote,
  Users, Sparkles, Link2, X, BarChart3,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const FOLDERS = [
  { id: 'teasers',      label: 'Teasers',      icon: Video,     color: 'text-purple-700' },
  { id: 'feature_posts',label: 'Feature Posts',icon: Sparkles,  color: 'text-blue-700' },
  { id: 'engagement',   label: 'Engagement',   icon: ImageIcon, color: 'text-emerald-700' },
  { id: 'quotes',       label: 'Quotes',       icon: Quote,     color: 'text-amber-700' },
  { id: 'testimonials', label: 'Testimonials', icon: Users,     color: 'text-rose-700' },
];

const PLATFORMS = ['all', 'instagram', 'linkedin', 'tiktok', 'x', 'facebook', 'youtube'];

export const SocialMediaStudioTab = () => {
  const [folder, setFolder] = useState('teasers');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ file: null, title: '', platform: 'instagram', caption: '', tags: '', dimensions: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [gen, setGen] = useState({ feature_name: '', benefit: '', platform: 'instagram', accent: 'blue' });
  const [generating, setGenerating] = useState(false);

  // v6.38.12 — Phase 3: Attribution dashboard
  const [view, setView] = useState('library'); // 'library' | 'attribution'
  const [attr, setAttr] = useState(null);
  const [attrDays, setAttrDays] = useState(30);
  const [attrLoading, setAttrLoading] = useState(false);

  const loadAttribution = async (days = attrDays) => {
    setAttrLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/social-media/attribution?days=${days}`, H()
      );
      setAttr(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Attribution load failed');
    }
    setAttrLoading(false);
  };

  useEffect(() => {
    if (view === 'attribution') loadAttribution(attrDays);
    // eslint-disable-next-line
  }, [view, attrDays]);

  const H = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

  const load = async () => {
    setLoading(true);
    try {
      const [listR, countsR] = await Promise.all([
        axios.get(`${API}/api/social-media/assets?folder=${folder}`, H()),
        axios.get(`${API}/api/social-media/folders/counts`, H()),
      ]);
      setItems(listR.data.items || []);
      setCounts(countsR.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [folder]);

  const upload = async () => {
    if (!form.file || !form.title) return toast.error('File and title required');
    setSubmitting(true);
    const fd = new FormData();
    fd.append('file', form.file);
    fd.append('folder', folder);
    fd.append('title', form.title);
    fd.append('platform', form.platform);
    fd.append('caption', form.caption);
    fd.append('tags', form.tags);
    fd.append('dimensions', form.dimensions);
    try {
      await axios.post(`${API}/api/social-media/assets`, fd, {
        ...H(),
        headers: { ...H().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Uploaded');
      setForm({ file: null, title: '', platform: 'instagram', caption: '', tags: '', dimensions: '' });
      setShowUpload(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setSubmitting(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this asset? Also removes the file from R2.')) return;
    try {
      await axios.delete(`${API}/api/social-media/assets/${id}`, H());
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const copyCaption = (caption) => {
    navigator.clipboard?.writeText(caption || '');
    toast.success('Caption copied');
  };

  const copyShareLink = async (id, platform) => {
    try {
      const r = await axios.get(`${API}/api/social-media/assets/${id}/utm?platform=${platform}`, H());
      navigator.clipboard?.writeText(r.data.share_url);
      toast.success('UTM-tagged share link copied');
    } catch (e) {
      toast.error('Failed to build share link');
    }
  };

  const generatePost = async () => {
    if (!gen.feature_name || !gen.benefit) return toast.error('Feature name and benefit required');
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/social-media/generate-feature-post`, gen, H());
      toast.success(`Generated: ${r.data.asset.title}`);
      setGen({ feature_name: '', benefit: '', platform: 'instagram', accent: 'blue' });
      setShowGen(false);
      setFolder('feature_posts');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed — try again in 30 sec');
    }
    setGenerating(false);
  };

  const currentFolder = FOLDERS.find(f => f.id === folder);

  return (
    <div className="space-y-4" data-testid="social-media-studio-tab">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Social Media Studio</h2>
          <p className="text-sm text-slate-600">Central library for social-media collateral. Reps download or copy UTM-tagged share links.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-full border-2 border-slate-200 p-1 bg-white">
            <button
              onClick={() => setView('library')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                view === 'library' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="sm-view-library"
            >
              Library
            </button>
            <button
              onClick={() => setView('attribution')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition inline-flex items-center gap-1 ${
                view === 'attribution' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="sm-view-attribution"
            >
              <BarChart3 size={14} /> Attribution
            </button>
          </div>
          {view === 'library' && (
            <>
              <Button onClick={() => setShowUpload(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sm-upload-btn">
                <Upload size={16} className="mr-2" /> Upload Asset
              </Button>
              <Button onClick={() => setShowGen(true)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="sm-generate-btn">
                <Sparkles size={16} className="mr-2" /> Generate Feature Post
              </Button>
            </>
          )}
        </div>
      </div>

      {view === 'attribution' && (
        <Card className="p-5 border-2 border-indigo-200 bg-white" data-testid="sm-attribution-panel">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <BarChart3 size={18} className="text-indigo-700" />
                Click &amp; Conversion Attribution
              </h3>
              <p className="text-xs text-slate-600 mt-1">
                Per-rep and per-post breakdown of shared social-media links. Only counts visits with a <code>utm_source</code> tag.
              </p>
            </div>
            <div className="flex gap-2">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setAttrDays(d)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 ${
                    attrDays === d
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-900'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`sm-attr-window-${d}d`}
                >
                  Last {d}d
                </button>
              ))}
            </div>
          </div>

          {attrLoading && <div className="text-sm text-slate-500 py-6">Loading…</div>}

          {!attrLoading && attr && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatTile label="Clicks" value={attr.totals?.clicks || 0} testid="sm-attr-clicks" />
                <StatTile label="Unique visitors" value={attr.totals?.unique_visitors || 0} testid="sm-attr-visitors" />
                <StatTile label="Conversions" value={attr.totals?.conversions || 0} testid="sm-attr-conversions" />
                <StatTile label="Conv. rate" value={`${attr.totals?.conversion_rate_pct || 0}%`} testid="sm-attr-rate" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-bold text-slate-900 mb-2">Per Rep</h4>
                  <div className="border border-slate-200 rounded overflow-hidden">
                    <table className="w-full text-sm" data-testid="sm-attr-per-rep">
                      <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                        <tr>
                          <th className="text-left px-3 py-2">Rep</th>
                          <th className="text-right px-3 py-2">Clicks</th>
                          <th className="text-right px-3 py-2">Conv.</th>
                          <th className="text-right px-3 py-2">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(attr.per_rep || []).length === 0 && (
                          <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">No rep clicks yet</td></tr>
                        )}
                        {(attr.per_rep || []).map(r => (
                          <tr key={r.rep} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-medium">{r.rep}</td>
                            <td className="px-3 py-2 text-right">{r.clicks}</td>
                            <td className="px-3 py-2 text-right">{r.conversions}</td>
                            <td className="px-3 py-2 text-right">{r.conversion_rate_pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-slate-900 mb-2">Per Platform</h4>
                  <div className="border border-slate-200 rounded overflow-hidden">
                    <table className="w-full text-sm" data-testid="sm-attr-per-platform">
                      <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                        <tr>
                          <th className="text-left px-3 py-2">Platform</th>
                          <th className="text-right px-3 py-2">Clicks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(attr.per_platform || []).length === 0 && (
                          <tr><td colSpan={2} className="px-3 py-4 text-center text-slate-400">No platform data yet</td></tr>
                        )}
                        {(attr.per_platform || []).map(p => (
                          <tr key={p.platform} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-medium capitalize">{p.platform}</td>
                            <td className="px-3 py-2 text-right">{p.clicks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <h4 className="text-sm font-bold text-slate-900 mb-2">Top posts</h4>
                <div className="border border-slate-200 rounded overflow-hidden">
                  <table className="w-full text-sm" data-testid="sm-attr-per-asset">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">Post</th>
                        <th className="text-left px-3 py-2">Platform</th>
                        <th className="text-right px-3 py-2">Clicks</th>
                        <th className="text-right px-3 py-2">Conv.</th>
                        <th className="text-right px-3 py-2">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(attr.per_asset || []).length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">No post clicks yet</td></tr>
                      )}
                      {(attr.per_asset || []).map(a => (
                        <tr key={a.asset_id} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-medium">{a.title}</td>
                          <td className="px-3 py-2 capitalize text-slate-600">{a.platform || '—'}</td>
                          <td className="px-3 py-2 text-right">{a.clicks}</td>
                          <td className="px-3 py-2 text-right">{a.conversions}</td>
                          <td className="px-3 py-2 text-right">{a.conversion_rate_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </Card>
      )}

      {view === 'library' && (
      <>

      {/* Folder tabs */}
      <div className="flex flex-wrap gap-2">
        {FOLDERS.map(f => {
          const Icon = f.icon;
          const active = f.id === folder;
          return (
            <button
              key={f.id}
              onClick={() => setFolder(f.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm transition ${
                active
                  ? 'border-blue-600 bg-blue-50 text-blue-900 font-semibold'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
              data-testid={`sm-folder-${f.id}`}
            >
              <Icon size={14} className={f.color} />
              {f.label}
              <Badge className="bg-slate-200 text-slate-700 text-xs">{counts[f.id] || 0}</Badge>
            </button>
          );
        })}
      </div>

      {/* Upload modal */}
      {showUpload && (
        <Card className="p-5 border-2 border-blue-400 bg-blue-50">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-bold text-blue-900">Upload to <span className="italic">{currentFolder?.label}</span></h3>
            <button onClick={() => setShowUpload(false)} className="text-slate-500"><X size={16} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs uppercase text-slate-500">File (image/video, ≤ 100 MB)</label>
              <input type="file" onChange={e => setForm({ ...form, file: e.target.files[0] })} className="mt-1 w-full text-sm" data-testid="sm-file-input" />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">Title</label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="sm-title-input" />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">Platform</label>
              <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="mt-1 w-full border rounded px-2 py-2" data-testid="sm-platform-select">
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">Dimensions</label>
              <Input value={form.dimensions} onChange={e => setForm({ ...form, dimensions: e.target.value })} placeholder="e.g., 1080x1080" className="mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">Tags (comma-separated)</label>
              <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="e.g., k-2, wida, listening" className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase text-slate-500">Caption / Copy</label>
              <Textarea value={form.caption} onChange={e => setForm({ ...form, caption: e.target.value })} rows={3} className="mt-1" data-testid="sm-caption-input" />
            </div>
          </div>
          <Button onClick={upload} disabled={submitting} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" data-testid="sm-upload-submit">
            {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload className="mr-2" size={16} />}
            Upload
          </Button>
        </Card>
      )}

      {/* Generate Feature Post modal */}
      {showGen && (
        <Card className="p-5 border-2 border-purple-400 bg-purple-50">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="font-bold text-purple-900">Generate a Feature Post</h3>
              <p className="text-xs text-purple-700">MOSAIC-generated branded card saved to Feature Posts folder (~15s).</p>
            </div>
            <button onClick={() => setShowGen(false)} className="text-slate-500"><X size={16} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs uppercase text-slate-500">Feature name (shows as headline)</label>
              <Input value={gen.feature_name} onChange={e => setGen({ ...gen, feature_name: e.target.value })} placeholder="e.g., K-2 Color Pictographs" className="mt-1" data-testid="sm-gen-name" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase text-slate-500">Benefit (1-2 sentence value prop)</label>
              <Textarea value={gen.benefit} onChange={e => setGen({ ...gen, benefit: e.target.value })} rows={2} placeholder="e.g., Pre-readers point to answers with color-coded icons — no reading required." className="mt-1" data-testid="sm-gen-benefit" />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">Platform</label>
              <select value={gen.platform} onChange={e => setGen({ ...gen, platform: e.target.value })} className="mt-1 w-full border rounded px-2 py-2" data-testid="sm-gen-platform">
                <option value="instagram">Instagram (1080x1080)</option>
                <option value="linkedin">LinkedIn (1200x627)</option>
                <option value="x">X / Twitter (1600x900)</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">Accent color</label>
              <select value={gen.accent} onChange={e => setGen({ ...gen, accent: e.target.value })} className="mt-1 w-full border rounded px-2 py-2" data-testid="sm-gen-accent">
                <option value="blue">MOSAIC Blue</option>
                <option value="purple">Purple</option>
                <option value="green">Green</option>
                <option value="amber">Amber</option>
              </select>
            </div>
          </div>
          <Button onClick={generatePost} disabled={generating} className="mt-4 bg-purple-600 hover:bg-purple-700 text-white" data-testid="sm-gen-submit">
            {generating ? <><Loader2 className="animate-spin mr-2" size={16} /> Generating…</> : <><Sparkles className="mr-2" size={16} /> Generate & Save</>}
          </Button>
        </Card>
      )}

      {/* Grid */}
      {loading ? (
        <Loader2 className="animate-spin text-blue-600 mx-auto" size={32} />
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">
          <ImageIcon className="mx-auto text-slate-300 mb-2" size={48} />
          <div className="font-semibold text-slate-700">No assets in {currentFolder?.label} yet</div>
          <div className="text-sm mt-1">Click <b>Upload Asset</b> above to add your first.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(a => {
            const isVideo = (a.content_type || '').startsWith('video');
            return (
              <Card key={a.id} className="overflow-hidden flex flex-col" data-testid={`sm-asset-${a.id}`}>
                <div className="aspect-video bg-slate-100 flex items-center justify-center">
                  {isVideo ? (
                    <video src={a.asset_url} controls className="w-full h-full object-contain" />
                  ) : (
                    <img src={a.asset_url} alt={a.title} className="w-full h-full object-contain" />
                  )}
                </div>
                <div className="p-3 space-y-2 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm text-slate-900 flex-1 truncate">{a.title}</div>
                    <Badge className="bg-blue-100 text-blue-800 text-xs shrink-0">{a.platform}</Badge>
                  </div>
                  {a.dimensions && <div className="text-xs text-slate-500">{a.dimensions}</div>}
                  {a.caption && (
                    <div className="text-xs text-slate-700 line-clamp-3 italic border-l-2 border-slate-200 pl-2">
                      {a.caption}
                    </div>
                  )}
                  {a.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {a.tags.map(t => <Badge key={t} className="bg-slate-100 text-slate-600 text-xs">#{t}</Badge>)}
                    </div>
                  )}
                  <div className="flex gap-1 mt-auto pt-2">
                    <Button size="sm" variant="outline" onClick={() => window.open(a.asset_url, '_blank')} title="Download / open">
                      <Upload size={12} className="rotate-180" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyCaption(a.caption)} title="Copy caption" disabled={!a.caption}>
                      <Copy size={12} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyShareLink(a.id, a.platform === 'all' ? 'linkedin' : a.platform)} title="Copy UTM share link">
                      <Link2 size={12} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(a.id)} title="Delete" className="text-rose-600 hover:bg-rose-50 ml-auto">
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
};

const StatTile = ({ label, value, testid }) => (
  <div className="bg-slate-50 border border-slate-200 rounded p-3" data-testid={testid}>
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
  </div>
);

export default SocialMediaStudioTab;
